import type { Asset } from 'types/asset.types';

type PropertyPath<T> = (obj: T) => number | undefined;

interface FindAssetOptions {
  getValue?: PropertyPath<Asset>;
  haulers?: boolean;
}

export function findAssetWithMaxValue(
  assets: Asset[],
  {
    getValue = (asset) => asset.productivity?.avg_load_per_hour,
    haulers = false,
  }: FindAssetOptions = {}
): Asset | undefined {
  if (assets.length === 0) {
    return undefined;
  }

  const findMax = (list: Asset[]): Asset | undefined => {
    return list.reduce((maxAsset, currentAsset) => {
      const maxValue = getValue(maxAsset);
      const currentValue = getValue(currentAsset);

      if (maxValue === undefined && currentValue === undefined) return maxAsset;
      if (maxValue === undefined) return currentAsset;
      if (currentValue === undefined) return maxAsset;

      return currentValue > maxValue ? currentAsset : maxAsset;
    });
  };

  if (haulers) {
    const haulTrucks = assets.filter((a) => a.assetType === 'haul_truck');
    if (haulTrucks.length > 0) {
      const maxHaulTruck = findMax(haulTrucks);
      const maxHaulTruckValue = maxHaulTruck
        ? getValue(maxHaulTruck)
        : undefined;
      if (maxHaulTruckValue && maxHaulTruckValue > 0) {
        return maxHaulTruck;
      }
    }
  }

  return findMax(assets);
}

export interface SensorReading {
  device_sensor: string;
  ts: number | null;
  value: number | null;
}

export type DeviceSensors = Record<string, SensorReading>;

export type MetricData = Record<string, DeviceSensors>;

interface TPMSData {
  temperature: MetricData;
  pressure: MetricData;
  assetTires: any[];
}

export function findDeviceWithMostTpmSData(data: TPMSData): string | null {
  const deviceCounts: Record<string, number> = {};

  const devices = data.pressure;

  for (const deviceKey in devices) {
    if (!deviceCounts[deviceKey]) {
      deviceCounts[deviceKey] = 0;
    }

    const sensors = devices[deviceKey];

    for (const sensorId in sensors) {
      const sensor = sensors[sensorId];
      if (sensor.value !== null && sensor.ts !== null) {
        deviceCounts[deviceKey]++;
      }
    }
  }

  let maxDevice: string | null = null;
  let maxCount = 0;

  for (const deviceKey in deviceCounts) {
    if (deviceCounts[deviceKey] > maxCount) {
      maxCount = deviceCounts[deviceKey];
      maxDevice = deviceKey;
    }
  }

  return maxDevice;
}
